import { Fetcher, objectToFormData, HTTPError } from '@this/src/util';
/* eslint-disable max-lines */
import React, { useState, useEffect, useContext } from 'react';
import { observer } from 'mobx-react';
import { css } from 'styled-components';
import { styled } from '@this/constants/themes';
import A from '@this/shared/atoms/a';
import type UserJson from '@this/domain/user/user_json';
import User from '@this/domain/user/user';
import Organization from '@this/domain/organization/organization2';
import { isWithinSixMonth, isOverDate } from '@this/components/shared/checker/date_checker';
import { isIncludeHyphen } from '@this/components/shared/checker/hyphen_checker';
import SelectNationalities from '@this/shared/nationality/select_nationalities';
import _ from 'lodash';
import Context from '@this/components/core_app/core_app_context';
import { Box } from '@material-ui/core';
import { Button } from '@this/shared/ui/inputs/button';
import { getColor } from '@this/shared/ui/theme';
import { Select } from '@this/shared/ui/inputs/select';
import moment from 'moment/moment';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { reportError } from '@this/lib/bugsnag';
import type { UserAuthenticate } from '@this/src/domain/user_authenticate/user_authenticate';
import { AutocompleteSelect } from '@this/shared/ui/inputs/autocomplete';
import type { ExicCardArgs } from '@this/domain/exic_card';
import { Checkbox } from '@this/shared/ui/inputs/checkbox/checkbox';
import { EditRegistrationForm } from './edit_registration_form';
import { AuthKeyModal } from './auth_key_modal';

interface Props {}

const DEFAULT_BIRTHDAYYEAR = '2000' as const;

const UserEdit: React.FC<Props> = observer(() => {
  const context = useContext(Context);

  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [editNotice, setEditNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editErrors, setEditErrors] = useState<string[]>([]);
  const [showAuthKeyModal, setShowAuthKeyModal] = useState(false);
  const [showEditRegistration, setShowEditRegistration] = useState(false);
  const [registrationSubmitting, setRegistrationSubmitting] = useState(false);
  const [editRegistrationErrors, setEditRegistrationErrors] = useState<string[]>([]);
  const [freeeAuthUri, setFreeeAuthUri] = useState('');
  const [freeeWorkflow, setFreeeWorkflow] = useState(false);
  const [restrictionDistanceAndTimeAvailability, setRestrictionDistanceAndTimeAvailability] = useState(false);
  const [birthdayYear, setBirthdayYear] = useState<string>(DEFAULT_BIRTHDAYYEAR);
  const [birthdayMonth, setBirthdayMonth] = useState('');
  const [birthdayDay, setBirthdayDay] = useState('');
  const [exicPasswordType, setExicPasswordType] = useState('password');
  const [userAuthenticate, setUserAuthenticate] = useState<UserAuthenticate[]>([]);
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [useSlackApp, setUseSlackApp] = useState(false);

  useEffect(() => {
    Fetcher.get<UserJson>('/users.json').then(
      response => {
        const user = new User(response);
        const organization = new Organization(user.organization);

        // birthday未設定の時は空文字
        const year = user.birthday === '' ? DEFAULT_BIRTHDAYYEAR : user.birthday.match(/^\d{4}/)![0];
        const month = user.birthday === '' ? '' : `${parseInt(user.birthday.match(/-(\d{2})-/)![1], 10)}`;
        const day = user.birthday === '' ? '' : `${parseInt(user.birthday.match(/(\d{2})$/)![1], 10)}`;

        setUser(user);
        setOrganization(organization);
        setFreeeAuthUri(response.freee_auth_uri);
        setFreeeWorkflow(response.freee_workflow);
        setRestrictionDistanceAndTimeAvailability(response.restriction_distance_and_time_availability);
        setBirthdayYear(year);
        setBirthdayMonth(month);
        setBirthdayDay(day);
        setGoogleCalendarConnected(response.google_calendar_connected);
        setUseSlackApp(response.use_slack_app);
      },
      () => {
        setUser(null);
        setGoogleCalendarConnected(false);
        setUseSlackApp(false);
      }
    );

    Fetcher.get<UserAuthenticate[]>('/user_authenticates.json').then(
      response => {
        setUserAuthenticate(response);
      },
      () => {
        setUserAuthenticate([]);
      }
    );
  }, []);

  const toggleAuthKeyModal = () => {
    setShowAuthKeyModal(!showAuthKeyModal);
  };

  const showEditRegistrationForm = () => {
    setShowEditRegistration(true);
  };

  const hideEditRegistrationForm = () => {
    setShowEditRegistration(false);
    setEditRegistrationErrors([]);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValidFormContent()) {
      return;
    }
    setSubmitting(true);
    setEditErrors([]);
    setEditNotice(null);
    updateAccount();
  };

  /* formの内容が正しければtrueを返す */
  const isValidFormContent = () => {
    const invalidErrors = [];
    if (user && isIncludeHyphen(user.tel)) {
      invalidErrors.push('電話番号はハイフンを含めず入力してください');
    }
    if (!isValidBirthday(birthdayYear, birthdayMonth, birthdayDay)) {
      invalidErrors.push('生年月日は正しい形式で入力してください');
    }
    if (organization?.showEx && user?.exicId && !/^\d{10}$/.test(user.exicId)) {
      invalidErrors.push('EXIC会員IDは半角数字10桁で入力してください');
    }
    if (invalidErrors.length > 0) {
      setEditErrors(invalidErrors);
      setEditNotice(null);
      return false;
    }
    return true;
  };

  const isValidBirthday = (year: string, month: string, day: string) => {
    if (year && month && day) {
      const mo = moment().set({ year: Number(year), month: Number(month) - 1, date: Number(day) });
      return mo.isValid();
    }
    return false;
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setBirthdayYear(e.target.value);

    const year = e.target.value;
    const month = birthdayMonth;
    const day = birthdayDay;
    if (isValidBirthday(year, month, day)) {
      const mo = moment().set({ year: Number(year), month: Number(month) - 1, date: Number(day) });
      user?.setBirthdayMoment(mo);
    }
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setBirthdayMonth(e.target.value);

    const year = birthdayYear;
    const month = e.target.value;
    const day = birthdayDay;
    if (isValidBirthday(year, month, day)) {
      const mo = moment().set({ year: Number(year), month: Number(month) - 1, date: Number(day) });
      user?.setBirthdayMoment(mo);
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setBirthdayDay(e.target.value);

    const year = birthdayYear;
    const month = birthdayMonth;
    const day = e.target.value;
    if (isValidBirthday(year, month, day)) {
      const mo = moment().set({ year: Number(year), month: Number(month) - 1, date: Number(day) });
      user?.setBirthdayMoment(mo);
    }
  };

  const fetchFreeeAuthUri = () => {
    Fetcher.get<UserJson>('/organization/freee/auth_uri.json').then(response => {
      setFreeeAuthUri(response.freee_auth_uri);
    });
  };

  const disconnectFreee = () => {
    Fetcher.delete('/organization/freee/member_tokens').then(() => {
      fetchFreeeAuthUri();
    });
  };

  const updateAccount = async () => {
    const params: { [key: string]: any } = {
      'user[user_id]': user!.id,
      'user[last_name]': user!.lastName,
      'user[first_name]': user!.firstName,
      'user[last_name_roman]': user!.lastNameRoman,
      'user[first_name_roman]': user!.firstNameRoman,
      'user[last_name_kana]': user!.lastNameKana,
      'user[first_name_kana]': user!.firstNameKana,
      'user[birthday]': user!.birthday,
      'user[nationality_id]': user!.nationalityId,
      'user[gender]': user!.gender,
      'user[tel]': user!.tel,
      'user[admin_notification]': user!.adminNotification,
      'user[exic_id]': user!.exicId,
      'user[exic_password]': user!.exicPassword,
      'user[exic_card_id]': user!.exicCardId,
      'user[passport_number]': user!.passportNumber,
      'user[passport_expire]': user!.passportExpire,
      'user[auto_sync_trip_to_calendar]': user!.autoSyncTripToCalendar,
      'organization[organization_name]': organization!.name,
      'organization[url]': organization!.url,
      'organization[address]': organization!.address,
      'organization[organization_tel]': organization!.tel
    };
    user!.mileageNumbers.forEach((mileageNumber, i) => {
      if (mileageNumber.number) {
        params[`user[mileage_numbers][${i}]`] = mileageNumber;
      }
    });
    user!.userForwardedEmails.forEach((userForwardedEmail, i) => {
      if (userForwardedEmail.email) {
        params[`user[user_forwarded_emails][${i}]`] = userForwardedEmail.params();
      }
    });
    try {
      await Fetcher.put('/users/update_account.json', objectToFormData(params));
      setEditErrors([]);
      setEditNotice('変更を保存しました');
      setSubmitting(false);
    } catch (e) {
      if (e instanceof HTTPError) {
        if (e.response && (e.response.status === 400 || e.response.status === 422)) {
          setEditErrors(e.response.data.errors);
          setSubmitting(false);
        } else {
          setEditErrors(['通信環境が不安定です。\n時間をおいてもう一度お試しください。']);
          setSubmitting(false);
        }
      } else {
        setEditErrors(['予期しないエラーが発生しました。']);
        setSubmitting(false);
      }
    }
  };

  const handleAddAuthKey = async (usage: string) => {
    try {
      const response = await Fetcher.post<UserAuthenticate>('/user_authenticates', { usage });
      setUserAuthenticate(prevState =>
        // [...prevState, response] スプレッド構文が使えないのでconcatで代用 (ES6に変更したら修正)
        prevState.concat(response)
      );
    } catch (e) {
      console.error('Failed to add auth key:', e);
    }
  };

  const handleDeleteAuthKey = async (id: number) => {
    // eslint-disable-next-line no-alert
    const isConfirmed = window.confirm('認証キーを削除しますか？');
    if (!isConfirmed) return;

    try {
      await Fetcher.delete(`/user_authenticates/${id}`);
      setUserAuthenticate(prevState => prevState.filter(key => key.id !== id));
    } catch (e) {
      console.error('Failed to delete auth key:', e);
    }
  };

  const handleChangeCurrentPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentPassword(e.target.value);
  };

  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleChangePasswordConfirmation = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordConfirmation(e.target.value);
  };

  const handleUpdateRegistrationSubmit = () => {
    setRegistrationSubmitting(true);
    setEditRegistrationErrors([]);
    setEditNotice(null);
    updateRegistration();
  };

  const handleNationalityIdChange = (id: number) => {
    user!.setNationalityId(id);
  };

  const handleExicPasswordType = () => {
    if (exicPasswordType === 'password') {
      setExicPasswordType('text');
      return;
    }
    setExicPasswordType('password');
  };

  const updateRegistration = async () => {
    const params = {
      'user[user_id]': user!.id,
      'user[current_password]': currentPassword,
      'user[password]': password,
      'user[password_confirmation]': passwordConfirmation
    };
    try {
      await Fetcher.put('/users', objectToFormData(params));
      setEditNotice('パスワードを変更しました');
      setEditErrors([]);
      setRegistrationSubmitting(false);
      setShowEditRegistration(false);
    } catch (e) {
      if (e instanceof HTTPError) {
        if (e.response && e.response.status === 400) {
          setEditRegistrationErrors(e.response.data.errors);
          setRegistrationSubmitting(false);
        } else {
          setEditRegistrationErrors(['通信環境が不安定です。\n時間をおいてもう一度お試しください。']);
          setRegistrationSubmitting(false);
        }
      } else {
        setEditRegistrationErrors(['予期しないエラーが発生しました。']);
        setRegistrationSubmitting(false);
      }
    }
  };

  const handleDisconnectGoogleCalendar = async () => {
    try {
      await Fetcher.delete('/users/calendar_auth/google.json');
      setGoogleCalendarConnected(false);
      setEditRegistrationErrors([]);
    } catch (e) {
      if (e instanceof HTTPError && e.response) {
        setEditRegistrationErrors([e.response.data?.error || 'Googleカレンダー連携解除に失敗しました']);
      } else {
        setEditRegistrationErrors(['Googleカレンダー連携解除に失敗しました']);
      }
    }
  };

  const handleToggleAutoSyncTripToCalendar = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (user) {
      user.setAutoSyncTripToCalendar(checked);
    }
  };

  try {
    const carries = ['ANA', 'JAL', 'SFJ', 'ADO', 'SNA', 'AXM', 'その他'];
    const dateOfMonth = user?.birthdayMoment?.daysInMonth() || 31;
    return (
      <div
        style={{
          flexGrow: 9999,
          display: 'flex',
          flexDirection: 'column',
          width: '100%'
        }}
      >
        <div
          style={{
            flexGrow: 9999,
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '600px',
            width: '100%',
            minHeight: '0%',
            marginLeft: 'auto',
            marginRight: 'auto',
            background: 'white'
          }}
        >
          {user && (
            <form onSubmit={handleSubmit}>
              <Section>
                <LeftLabel>氏名</LeftLabel>
                <SubLabel htmlFor="lastName">氏</SubLabel>
                <InputSplit
                  type="text"
                  id="lastName"
                  placeholder="例） 山田"
                  value={user.lastName}
                  onChange={e => user.setLastName(e.target.value)}
                />
                <SubLabel htmlFor="firstName">名</SubLabel>
                <InputSplit
                  type="text"
                  id="firstName"
                  placeholder="例） 一郎"
                  value={user.firstName}
                  onChange={e => user.setFirstName(e.target.value)}
                />
              </Section>
              <Section>
                <LeftLabel>氏名ローマ字</LeftLabel>
                <SubLabel htmlFor="lastNameRoman">氏</SubLabel>
                <InputSplit
                  type="text"
                  id="lastNameRoman"
                  placeholder="例） Yamada"
                  value={user.lastNameRoman}
                  onChange={e => user.setLastNameRoman(e.target.value)}
                />
                <SubLabel htmlFor="firstNameRoman">名</SubLabel>
                <InputSplit
                  type="text"
                  id="firstNameRoman"
                  placeholder="例） Ichiro"
                  value={user.firstNameRoman}
                  onChange={e => user.setFirstNameRoman(e.target.value)}
                />
              </Section>
              <Section>
                <LeftLabel>氏名カナ</LeftLabel>
                <SubLabel htmlFor="lastNameKana">氏</SubLabel>
                <InputSplit
                  type="text"
                  id="lastNameKana"
                  placeholder="例） ヤマダ"
                  value={user.lastNameKana}
                  onChange={e => user.setLastNameKana(e.target.value)}
                />
                <SubLabel htmlFor="firstNameKana">名</SubLabel>
                <InputSplit
                  type="text"
                  id="firstNameKana"
                  placeholder="例） イチロウ"
                  value={user.firstNameKana}
                  onChange={e => user.setFirstNameKana(e.target.value)}
                />
              </Section>
              <Field>
                <LeftLabel>生年月日</LeftLabel>
                <Select value={birthdayYear} onChange={handleYearChange} style={{ marginRight: '5px' }}>
                  <option value="">-</option>
                  {_.range(1900, moment().year() + 2).map(y => (
                    <option value={y} key={y}>
                      {y}年
                    </option>
                  ))}
                </Select>
                <Select value={birthdayMonth} onChange={handleMonthChange} style={{ marginRight: '5px' }}>
                  <option value="">-</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                    <option value={m} key={m}>
                      {m}月
                    </option>
                  ))}
                </Select>
                <Select value={birthdayDay} onChange={handleDateChange}>
                  <option value="">-</option>
                  {
                    // [...Array(dateOfMonth)].map((u, i) => i + 1) スプレッド構文が使えないのでArray.fromで代用 (ES6に変更したら修正)
                    Array.from({ length: dateOfMonth }, (_, i) => i + 1).map(d => (
                      <option value={d} key={d}>
                        {d}日
                      </option>
                    ))
                  }
                </Select>
                {isValidBirthday(birthdayYear, birthdayMonth, birthdayDay) || (
                  <div className="error">日付を設定してください</div>
                )}
              </Field>
              <Section>
                <LeftLabel>国籍</LeftLabel>
                <SelectNationalities
                  nationalities={user.nationalities}
                  selectedId={user.nationalityId}
                  onSelect={handleNationalityIdChange}
                />
              </Section>
              <Section>
                <LeftLabel>性別</LeftLabel>
                <label>
                  <input
                    type="radio"
                    value="male"
                    checked={user.gender === 'male'}
                    onChange={() => user.setGender('male')}
                  />
                  <span>男</span>
                </label>
                <label>
                  <input
                    type="radio"
                    value="female"
                    checked={user.gender === 'female'}
                    onChange={() => user.setGender('female')}
                  />
                  <span>女</span>
                </label>
              </Section>
              {restrictionDistanceAndTimeAvailability && (
                <Section>
                  <LeftLabel>拠点</LeftLabel>
                  {user.organizationBase?.name}
                </Section>
              )}
              <Field>
                <LeftLabel htmlFor="tel">電話番号</LeftLabel>
                <Input
                  type="text"
                  id="tel"
                  placeholder="例） 09011112222"
                  value={user.tel}
                  onChange={e => user.setTel(e.target.value)}
                />
                {isIncludeHyphen(user.tel) && <div className="error">ハイフンを含めず入力してください</div>}
              </Field>
              {!user.organization?.setting?.disable_show_milage_number && (
                <Section>
                  <LeftLabel>マイレージ</LeftLabel>
                  <MileageItems>
                    {user.mileageNumbers.map((mileageNumber, i) => (
                      <MileageItem key={i}>
                        <select
                          value={mileageNumber.carrier}
                          onChange={e => user.setMilageCarrier(i, e.target.value)}
                        >
                          {carries.map((c, i) => (
                            <option key={i} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          id="mileage"
                          className="user-signin__input"
                          value={mileageNumber.number}
                          onChange={e => user.setMilageNumber(i, e.target.value)}
                          autoComplete="new-password"
                        />
                        <A
                          className="user-edit__section__mileage-items__remove"
                          onClick={() => user.removeMileageNumber(i)}
                        >
                          <img src="/images/close_search.png" />
                        </A>
                      </MileageItem>
                    ))}
                    <div className="user-edit__section__mileage-items__add">
                      <A onClick={() => user.addMileageNumber()}>+マイレージを追加</A>
                    </div>
                  </MileageItems>
                </Section>
              )}
              {(user.express_outsourcing_availability || organization?.showEx) &&
              organization?.exicUsageMode === 'user_based' ? (
                <>
                  <Section>
                    <LeftLabel htmlFor="exicId">EXIC会員ID</LeftLabel>
                    <Input
                      type="text"
                      id="exicId"
                      placeholder="半角数字10桁"
                      value={user.exicId}
                      onChange={e => user.setExicId(e.target.value)}
                    />
                  </Section>
                  <Section>
                    <LeftLabel htmlFor="exicPassword">EXICパスワード</LeftLabel>
                    <Input
                      type={exicPasswordType}
                      id="exicPassword"
                      placeholder="半角数字記号4-8桁"
                      value={user.exicPassword}
                      onChange={e => user.setExicPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <span onClick={handleExicPasswordType}>
                      {exicPasswordType === 'password' ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </span>
                  </Section>
                </>
              ) : organization?.exicUsageMode === 'card_based' ? (
                <Section>
                  <LeftLabel htmlFor="exicId">EXIC会員ID</LeftLabel>
                  <AutocompleteSelect
                    placeholder="未選択"
                    value={
                      (user?.exic_cards?.find(o => o.id === Number(user?.exicCardId)) as ExicCardArgs) ?? null
                    }
                    options={user.exic_cards}
                    getOptionLabel={o => o.exic_number}
                    onChange={(_, o) => user.setExicCardId(o?.id || null)}
                    disabled={!organization?.allowUserExicInput}
                  />
                </Section>
              ) : null}
              <Section>
                <LeftLabel htmlFor="passportNumber">パスポート番号</LeftLabel>
                <Input
                  type="text"
                  id="passportNumber"
                  placeholder="例）AA1234567"
                  value={user.passportNumber}
                  onChange={e => user.setPassportNumber(e.target.value)}
                />
              </Section>
              <Field>
                <LeftLabel htmlFor="passportExpire">有効期限</LeftLabel>
                <Input
                  type="text"
                  id="passportExpire"
                  placeholder="例）2025-01-01"
                  value={user.passportExpire || ''}
                  onChange={e => user.setPassportExpire(e.target.value)}
                />
                {isWithinSixMonth(user.passportExpire) && (
                  <div className="error">
                    有効期限が近づいてきています。
                    <br />
                    パスポートをご確認ください。
                  </div>
                )}
                {isOverDate(user.passportExpire) && (
                  <div className="error">
                    有効期限が過ぎています。
                    <br />
                    パスポートをご確認ください。
                  </div>
                )}
              </Field>
              {organization?.setting?.sendUserForwardedEmail && (
                <Field>
                  <LeftLabel htmlFor="user_forwarded_email">転送アドレス</LeftLabel>
                  {_.map(user.userForwardedEmails, (e, i) => (
                    <Input
                      key={`user_forwarded_email_${i}`}
                      type="text"
                      id="user_forwarded_email"
                      value={e.email}
                      placeholder="user@example.jp"
                      onChange={e => user.setUserForwardedEmails(e.target.value, i)}
                    />
                  ))}
                </Field>
              )}
              {freeeWorkflow && (
                <Field>
                  <LeftLabel htmlFor="freeeConnect">freee連携</LeftLabel>
                  {freeeAuthUri ? (
                    <Button isExternal href={freeeAuthUri} size="small">
                      freeeと接続する
                    </Button>
                  ) : (
                    <>
                      <Flex>連携中</Flex>
                      <Button onClick={disconnectFreee} size="small">
                        連携解除
                      </Button>
                    </>
                  )}
                </Field>
              )}
              {context.serviceId === 1 && useSlackApp && (
                <Field>
                  <LeftLabel htmlFor="SlackConnect">Slack連携</LeftLabel>
                  {!user.aiTravelSlackToken ? (
                    <Button isExternal href="/slackapp/ai_travel/start_slack_auth" size="small">
                      Slackと接続する
                    </Button>
                  ) : (
                    <Flex>
                      <div style={{ marginRight: '10px' }}>連携</div>
                      <Button isExternal href="/slackapp/ai_travel/remove_slack_auth" size="small">
                        連携解除
                      </Button>
                    </Flex>
                  )}
                </Field>
              )}
              {/* Googleのアプリレビューが終わるまで、本番環境にOAuth連携の機能は出さない */}
              {(context.env === 'development' || context.env === 'staging') && (
                <Field>
                  <LeftLabel htmlFor="calendarConnect">カレンダー連携</LeftLabel>
                  <Box>
                    <Box>
                      {googleCalendarConnected ? (
                        <Box style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ color: '#4caf50', marginLeft: 8, fontWeight: 'bold' }}>
                            Googleカレンダー連携済み✓
                          </span>
                          <Button
                            color="danger"
                            size="small"
                            style={{ marginLeft: 12 }}
                            onClick={handleDisconnectGoogleCalendar}
                          >
                            解除
                          </Button>
                        </Box>
                      ) : (
                        <Button isExternal href="/users/calendar_auth/google" size="small">
                          Googleカレンダー連携
                        </Button>
                      )}
                    </Box>
                    <Checkbox
                      checked={user.autoSyncTripToCalendar}
                      disabled={!googleCalendarConnected}
                      onChange={handleToggleAutoSyncTripToCalendar}
                      style={{ marginTop: '10px' }}
                    >
                      旅行商品を自動でカレンダーに登録する
                    </Checkbox>
                  </Box>
                </Field>
              )}
              <Field>
                <LeftLabel htmlFor="AuthKey">認証キー発行</LeftLabel>
                <Button size="small" onClick={toggleAuthKeyModal}>
                  認証キー発行
                </Button>
              </Field>
              <AuthKeyModal
                open={showAuthKeyModal}
                onAbort={toggleAuthKeyModal}
                userAuthenticates={userAuthenticate}
                onAdd={handleAddAuthKey}
                onDelete={handleDeleteAuthKey}
              />
              <A className="user-edit__registration-link" onClick={showEditRegistrationForm}>
                パスワードの変更
              </A>
              <EditRegistrationForm
                submitting={registrationSubmitting}
                open={showEditRegistration}
                onSubmit={handleUpdateRegistrationSubmit}
                onAbort={hideEditRegistrationForm}
                currentPassword={currentPassword}
                onChangeCurrentPassword={handleChangeCurrentPassword}
                password={password}
                onChangePassword={handleChangePassword}
                passwordConfirmation={passwordConfirmation}
                onChangePasswordConfirmation={handleChangePasswordConfirmation}
                errors={editRegistrationErrors}
              />

              {editNotice && (
                <div className="user-edit__notice">
                  <div className="notice">{editNotice}</div>
                </div>
              )}

              <div className="user-edit__errors">
                {editErrors.map((e, i) => (
                  <div className="error" key={i}>
                    {e.split('\n').map((l, j) => (
                      <p key={j}>{l}</p>
                    ))}
                  </div>
                ))}
              </div>

              {submitting ? (
                <img className="user-edit__loading" src="/images/loading.gif" width={50} height={50} />
              ) : (
                <Box ml={2} mr={2} mb={2}>
                  <Button type="submit" size="large" fullWidth>
                    保存
                  </Button>
                </Box>
              )}
            </form>
          )}
        </div>
      </div>
    );
  } catch (e) {
    if (e instanceof Error) {
      reportError(e);
    }
    return null;
  }
});

export const Section = styled.div`
  display: flex;
  border-bottom: 1px solid ${props => props.theme.linkColor};
  padding: 20px;
  & select {
    margin: 0 5px 0 0;
  }
`;

export const Field = styled.div`
  display: flex;
  align-items: flex-begin;
  flex-wrap: nowrap;
  border-bottom: 1px solid ${props => props.theme.linkColor};
  padding: 20px;
  & select {
    margin: 0 5px 0 0;
  }
`;

export const LeftLabel = styled.label`
  min-width: 120px;
  color: ${props => props.theme.grayTextColor};
  font-weight: bold;
  margin-right: 16px;
`;

export const SubLabel = styled.label`
  min-width: 20px;
`;

const MixinInput = css`
  &::placeholder {
    color: ${getColor('text', 'placeholder')};
  }

  border: 0;
  padding: 0;
  margin-bottom: 0;
  box-shadow: none;
  line-height: 15px;
  height: 20px;
  background: white;
`;

export const Input = styled.input`
  &&& {
    ${MixinInput};
    width: calc(100% - 120px);
  }
`;

const InputSplit = styled.input`
  &&& {
    ${MixinInput};
    width: calc(45% - (120px / 2));
    margin-right: 10px;
  }
`;

const MileageItems = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const MileageItem = styled.div`
  display: flex;
  align-items: center;
  margin-right: 10px;
  margin-bottom: 10px;
`;

const Flex = styled.div`
  display: flex;
  align-items: center;
`;

export default UserEdit;
